import type { FC } from 'react'
import type { CreateRewardCategoryDTO, UpdateRewardCategoryDTO } from 'types/rewards'
import type { ColumnDef } from '@tanstack/react-table'

import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import SimpleTitle from 'components/SimpleTitle'
import useAuth from 'hooks/useAuth'
import {
  useFetchRewardCategories,
  useCreateRewardCategory,
  useUpdateRewardCategory,
  useDeleteRewardCategory,
} from 'hooks/useRewards'

import { useSnackbar } from 'notistack'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThemeMode } from 'types/config'
import { getRootOrganizationId } from 'types/user-profile'

interface CategoryEdit {
  name: string
  displayOrder: number
}

interface NewCategory {
  tempId: string
  name: string
  displayOrder: number
}

interface DisplayCategory {
  id: string | undefined
  tempId: string | undefined
  name: string
  displayOrder: number
  isNew: boolean
  isDirty: boolean
}

interface DraggableRowProps {
  row: any
  hoverBackgroundColor: string
  isDraggable: boolean
}

const DraggableRow: FC<DraggableRowProps> = ({ row, hoverBackgroundColor, isDraggable }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: !isDraggable,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{ '&:hover': { backgroundColor: hoverBackgroundColor } }}
      {...attributes}
    >
      {row.getVisibleCells().map((cell: any) => (
        <TableCell
          key={cell.id}
          align={
            cell.column.id === 'displayOrder' || cell.column.id === 'actions' || cell.column.id === 'dragHandle'
              ? 'center'
              : 'left'
          }
          {...(cell.column.id === 'dragHandle' && isDraggable ? listeners : {})}
          sx={{
            ...(cell.column.id === 'dragHandle' && isDraggable && { cursor: 'grab' }),
          }}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  )
}

const EditCategories: FC = () => {
  const theme = useTheme()
  const hoverBackgroundColor =
    theme.palette.mode === ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.secondary.lighter

  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [organizationId, setOrganizationId] = useState<string>('')

  // Track edits to existing categories (id -> edited values)
  const [edits, setEdits] = useState<Record<string, CategoryEdit>>({})
  // Track new categories not yet saved
  const [newCategories, setNewCategories] = useState<NewCategory[]>([])
  // Saving state
  const [isSaving, setIsSaving] = useState(false)

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<{ id?: string; tempId?: string; name: string } | null>(null)

  // Fetch categories from SWR
  const {
    categories: fetchedCategories,
    isLoading,
    refetch,
  } = useFetchRewardCategories(rootOrganizationId, token!, !!rootOrganizationId && !!token)

  const { createCategory } = useCreateRewardCategory(token!)
  const { updateCategory } = useUpdateRewardCategory(token!)
  const { deleteCategory } = useDeleteRewardCategory(token!)

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
        setOrganizationId(orgId)
      }
    }
  }, [profile])

  // Compute display list: fetched categories with edits applied + new categories
  // Sort by displayOrder to reflect drag-and-drop changes
  const displayCategories = useMemo(() => {
    const existing = (fetchedCategories || []).map((cat) => {
      const catId = cat.id ?? undefined
      const edit = catId ? edits[catId] : undefined
      return {
        id: catId,
        tempId: undefined as string | undefined,
        name: edit?.name ?? cat.name,
        displayOrder: edit?.displayOrder ?? cat.displayOrder,
        isNew: false,
        isDirty: !!edit,
      }
    })

    const newCats = newCategories.map((cat) => ({
      id: undefined as string | undefined,
      tempId: cat.tempId,
      name: cat.name,
      displayOrder: cat.displayOrder,
      isNew: true,
      isDirty: true,
    }))

    const allCategories = [...existing, ...newCats]

    // Sort by displayOrder to maintain visual order
    const sorted = allCategories.sort((a, b) => a.displayOrder - b.displayOrder)
    console.log(
      'displayCategories updated:',
      sorted.map((c) => ({ name: c.name, order: c.displayOrder, isDirty: c.isDirty }))
    )
    return sorted
  }, [fetchedCategories, edits, newCategories])

  const handleNameChange = useCallback(
    (id: string | undefined, tempId: string | undefined, value: string) => {
      if (id) {
        // Edit existing category
        setEdits((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            name: value,
            displayOrder: prev[id]?.displayOrder ?? (fetchedCategories?.find((c) => c.id === id)?.displayOrder || 0),
          },
        }))
      } else if (tempId) {
        // Edit new category
        setNewCategories((prev) => prev.map((cat) => (cat.tempId === tempId ? { ...cat, name: value } : cat)))
      }
    },
    [fetchedCategories]
  )

  const handleAddCategory = useCallback(() => {
    const allOrders = [
      ...(fetchedCategories || []).map((c) => c.displayOrder),
      ...newCategories.map((c) => c.displayOrder),
    ]
    const maxOrder = allOrders.length > 0 ? Math.max(...allOrders) : 0

    setNewCategories((prev) => [
      ...prev,
      {
        tempId: `new-${Date.now()}`,
        name: '',
        displayOrder: Math.max(maxOrder + 1, 1),
      },
    ])
  }, [fetchedCategories, newCategories])

  const handleOpenDeleteDialog = useCallback((cat: { id?: string; tempId?: string; name: string; isNew: boolean }) => {
    if (cat.isNew && cat.tempId) {
      // Just remove from local state for new categories
      setNewCategories((prev) => prev.filter((c) => c.tempId !== cat.tempId))
      return
    }

    setCategoryToDelete({ id: cat.id, name: cat.name })
    setDeleteDialogOpen(true)
  }, [])

  const handleCloseDeleteDialog = useCallback(() => {
    setCategoryToDelete(null)
    setDeleteDialogOpen(false)
  }, [])

  const handleConfirmDeleteCategory = useCallback(async () => {
    if (!categoryToDelete?.id) return

    try {
      await deleteCategory(categoryToDelete.id)
      // Remove from edits if present
      setEdits((prev) => {
        const next = { ...prev }
        delete next[categoryToDelete.id!]
        return next
      })
      enqueueSnackbar('Category deleted successfully', { variant: 'success' })
      await refetch()
    } catch (error) {
      enqueueSnackbar('Failed to delete category', { variant: 'error' })
    } finally {
      handleCloseDeleteDialog()
    }
  }, [categoryToDelete, deleteCategory, enqueueSnackbar, refetch, handleCloseDeleteDialog])

  const handleSave = useCallback(async () => {
    // Validate
    const hasEmptyName = displayCategories.some((c) => !c.name.trim())
    if (hasEmptyName) {
      enqueueSnackbar('All categories must have a name', { variant: 'error' })
      return
    }

    setIsSaving(true)
    try {
      // Save edited existing categories
      for (const [id, edit] of Object.entries(edits)) {
        const dto: UpdateRewardCategoryDTO = {
          name: edit.name.trim(),
          displayOrder: edit.displayOrder,
        }
        await updateCategory(id, dto)
      }

      // Create new categories
      for (const cat of newCategories) {
        const dto: CreateRewardCategoryDTO = {
          rootOrganizationId,
          organizationId,
          name: cat.name.trim(),
          displayOrder: cat.displayOrder,
          isActive: true,
        }
        await createCategory(dto)
      }

      enqueueSnackbar('Categories saved successfully', { variant: 'success' })

      // Clear local edits and refetch
      setEdits({})
      setNewCategories([])
      await refetch()
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save categories'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setIsSaving(false)
    }
  }, [
    displayCategories,
    edits,
    newCategories,
    rootOrganizationId,
    organizationId,
    updateCategory,
    createCategory,
    enqueueSnackbar,
    refetch,
  ])

  const hasChanges = useMemo(() => {
    return Object.keys(edits).length > 0 || newCategories.length > 0
  }, [edits, newCategories])

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end to reorder categories
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = displayCategories.findIndex((cat) => (cat.id || cat.tempId) === active.id)
      const newIndex = displayCategories.findIndex((cat) => (cat.id || cat.tempId) === over.id)

      console.log('Drag: oldIndex=', oldIndex, 'newIndex=', newIndex)
      console.log(
        'Before drag:',
        displayCategories.map((c) => ({ name: c.name, order: c.displayOrder }))
      )

      if (oldIndex === -1 || newIndex === -1) {
        return
      }

      // Don't allow moving the first category (displayOrder === 0)
      if (displayCategories[oldIndex].displayOrder === 0) {
        return
      }

      // Don't allow moving to the first position (where displayOrder === 0 category is)
      if (newIndex === 0 && displayCategories[0].displayOrder === 0) {
        return
      }

      // Reorder the array
      const reordered = [...displayCategories]
      const [removed] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, removed)

      console.log(
        'After reorder:',
        reordered.map((c) => ({ name: c.name, order: c.displayOrder }))
      )

      // Update ALL display orders based on new positions
      // displayOrder should match the array index
      const newEdits: Record<string, CategoryEdit> = { ...edits }
      const updatedNewCategories: NewCategory[] = []

      reordered.forEach((cat, index) => {
        // displayOrder equals the position in the array
        const calculatedOrder = index

        if (cat.id) {
          // Always update existing category's displayOrder in edits
          newEdits[cat.id] = {
            name: cat.name,
            displayOrder: calculatedOrder,
          }
        } else if (cat.tempId) {
          // Update new category
          updatedNewCategories.push({
            tempId: cat.tempId,
            name: cat.name,
            displayOrder: calculatedOrder,
          })
        }
      })

      console.log('New edits:', newEdits)
      console.log('Updated new categories:', updatedNewCategories)

      setEdits(newEdits)
      setNewCategories(updatedNewCategories)
    },
    [displayCategories, edits]
  )

  // Define table columns
  const columns = useMemo<ColumnDef<DisplayCategory>[]>(
    () => [
      {
        id: 'name',
        header: 'Category Name',
        accessorKey: 'name',
        cell: ({ row }) => (
          <TextField
            variant="outlined"
            fullWidth
            value={row.original.name}
            onChange={(e) => handleNameChange(row.original.id, row.original.tempId, e.target.value)}
            placeholder="Category name"
            error={row.original.isDirty && !row.original.name.trim()}
          />
        ),
      },
      {
        id: 'displayOrder',
        header: 'Display Order',
        accessorKey: 'displayOrder',
        cell: ({ row }) => <Typography variant="body2">{row.original.displayOrder}</Typography>,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Tooltip title={row.original.displayOrder === 0 ? 'Cannot delete primary category' : 'Delete category'}>
            <span>
              <IconButton
                color="error"
                onClick={() => handleOpenDeleteDialog(row.original)}
                disabled={row.original.displayOrder === 0}
              >
                <DeleteOutlined />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
      {
        id: 'dragHandle',
        header: '',
        cell: ({ row }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {row.original.displayOrder !== 0 ? (
              <HolderOutlined style={{ cursor: 'grab', fontSize: '16px', opacity: 0.5 }} />
            ) : (
              <Box sx={{ width: '16px' }} />
            )}
          </Box>
        ),
      },
    ],
    [handleNameChange, handleOpenDeleteDialog]
  )

  // Create table instance
  const table = useReactTable({
    data: displayCategories,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id || row.tempId || '',
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Edit Categories" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Manage reward categories. Categories group related rewards together (e.g., "Health Benefits", "Retirement").
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3, fontStyle: 'italic' }}>
          Drag rows to reorder categories (Display Order is automatically updated). The "Compensation" category will
          always remain first.
        </Typography>

        {displayCategories.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              No categories yet. Add your first category to get started.
            </Typography>
          </Box>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <TableContainer sx={{ mb: 2 }}>
              <Table stickyHeader size="small">
                <TableHead
                  sx={{
                    '& th': {
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderBottom: `2px solid ${theme.palette.divider} !important`,
                    },
                  }}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableCell
                          key={header.id}
                          align={
                            header.id === 'displayOrder' || header.id === 'actions' || header.id === 'dragHandle'
                              ? 'center'
                              : 'left'
                          }
                          sx={{
                            minWidth:
                              header.id === 'dragHandle'
                                ? 50
                                : header.id === 'name'
                                  ? 250
                                  : header.id === 'displayOrder'
                                    ? 150
                                    : 100,
                            width: header.id === 'dragHandle' ? 50 : 'auto',
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <SortableContext
                  items={table.getRowModel().rows.map((row) => row.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {table.getRowModel().rows.map((row) => {
                      const isDraggable = row.original.displayOrder !== 0
                      return (
                        <DraggableRow
                          key={row.id}
                          row={row}
                          hoverBackgroundColor={hoverBackgroundColor}
                          isDraggable={isDraggable}
                        />
                      )
                    })}
                  </TableBody>
                </SortableContext>
              </Table>
            </TableContainer>
          </DndContext>
        )}

        <Button variant="outlined" startIcon={<PlusOutlined />} onClick={handleAddCategory}>
          Add Category
        </Button>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <AnimateButton>
            <Button
              variant="contained"
              color="warning"
              onClick={() => startTransition(() => navigate('/rewards/manage'))}
            >
              Cancel
            </Button>
          </AnimateButton>

          <AnimateButton>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving || !hasChanges}>
              {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
            </Button>
          </AnimateButton>
        </Stack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the category <strong>"{categoryToDelete?.name}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button variant="contained" color="warning" onClick={handleCloseDeleteDialog}>
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <Button variant="contained" color="error" onClick={handleConfirmDeleteCategory}>
              Delete
            </Button>
          </AnimateButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EditCategories
